import { useRef, type ReactNode } from "react";
import {
  GitPullRequest,
  GitCommit,
  MessageSquare,
  RefreshCw,
  CheckCircle2,
  ArrowRight,
  ShieldCheck,
  Activity,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { getHostLabel } from "@/lib/chat/cli-guidance";
import { useResolvedHost } from "@/hooks/use-resolved-host";
import { useScrollSpotlight } from "../scroll-spotlight";

import { CopyableTerminal } from "../copyable-terminal";

/* -------------------------------------------------------------------------- */
/*  Shared primitives                                                         */
/* -------------------------------------------------------------------------- */

function NumberedInstruction({ step, title, children }: { step: number; title: string; children: ReactNode }) {
  const ref = useRef<HTMLDivElement>(null);
  useScrollSpotlight(ref);
  return (
    <div ref={ref} className="flex gap-5 transition-opacity duration-500 ease-out">
      <div className="flex flex-col items-center">
        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-white/[0.08] bg-white/[0.05] text-sm font-semibold text-foreground">{step}</div>
        <div className="mt-3 w-px flex-1 bg-white/[0.06]" />
      </div>
      <div className="min-w-0 flex-1 pb-28">
        <p className="mt-1 text-lg font-semibold text-foreground">{title}</p>
        <div className="mt-4 space-y-4">{children}</div>
      </div>
    </div>
  );
}

function NumberedInstructionLast({ step, title, children }: { step: number; title: string; children: ReactNode }) {
  const ref = useRef<HTMLDivElement>(null);
  useScrollSpotlight(ref);
  return (
    <div ref={ref} className="flex gap-5 transition-opacity duration-500 ease-out">
      <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-white/[0.08] bg-white/[0.05] text-sm font-semibold text-foreground">{step}</div>
      <div className="min-w-0 flex-1 pb-12">
        <p className="mt-1 text-lg font-semibold text-foreground">{title}</p>
        <div className="mt-4 space-y-4">{children}</div>
      </div>
    </div>
  );
}

function InfoCard({ icon: Icon, title, description, accentClass }: { icon: React.ComponentType<{ className?: string }>; title: string; description: string; accentClass: string }) {
  return (
    <div className="rounded-xl border border-white/[0.06] bg-white/[0.03] p-4">
      <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-white/[0.06]">
        <Icon className={cn("h-5 w-5", accentClass)} />
      </div>
      <p className="mt-3 text-sm font-semibold text-foreground">{title}</p>
      <p className="mt-1.5 text-sm leading-6 text-muted-foreground">{description}</p>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*  Step 1 — What is PullRequest?                                             */
/* -------------------------------------------------------------------------- */

export function WhatIsPullRequestStep() {
  return (
    <div className="space-y-0">
      <NumberedInstruction step={1} title="What does SpecTree PullRequest do?">
        <p className="text-sm leading-7 text-muted-foreground">
          After the Orchestrator finishes implementing your epic, your code is sitting
          on a local branch. SpecTree PullRequest automates the entire PR lifecycle — it
          commits your changes, pushes to the remote, creates a pull request with a
          properly structured description, and then kicks off a{" "}
          <strong className="text-foreground/90">Copilot code review cycle</strong>.
        </p>
        <p className="text-sm leading-7 text-muted-foreground">
          It reads Copilot review comments, fixes the code issues, pushes the fixes, and
          re-requests a review — repeating this loop until the PR is clean.
        </p>
      </NumberedInstruction>

      <NumberedInstruction step={2} title="The review cycle">
        <p className="text-sm leading-7 text-muted-foreground">
          SpecTree PullRequest enforces a minimum of{" "}
          <strong className="text-foreground/90">3 full review cycles</strong>. Even if the
          first review comes back clean, it runs at least two more to make sure nothing
          was missed.
        </p>
        <div className="flex items-center gap-3 rounded-xl border border-white/[0.06] bg-white/[0.03] px-5 py-4">
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <GitCommit className="h-4 w-4 text-emerald-400" />
            <span>Commit &amp; push</span>
          </div>
          <ArrowRight className="h-4 w-4 text-muted-foreground/40" />
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <MessageSquare className="h-4 w-4 text-emerald-400" />
            <span>Copilot reviews</span>
          </div>
          <ArrowRight className="h-4 w-4 text-muted-foreground/40" />
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <RefreshCw className="h-4 w-4 text-amber-400" />
            <span>Fix &amp; re-review</span>
          </div>
          <ArrowRight className="h-4 w-4 text-muted-foreground/40" />
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <CheckCircle2 className="h-4 w-4 text-emerald-400" />
            <span>Clean PR</span>
          </div>
        </div>
      </NumberedInstruction>

      <NumberedInstructionLast step={3} title="What does it check?">
        <div className="grid gap-3 md:grid-cols-2">
          <InfoCard
            icon={MessageSquare}
            title="Copilot review"
            description="Reads every comment Copilot leaves, classifies them as must-fix or style nits, and only fixes the real issues."
            accentClass="text-emerald-400"
          />
          <InfoCard
            icon={Activity}
            title="CI status"
            description="Waits for GitHub Actions CI to pass. The PR is not considered complete until both Copilot review and CI are green."
            accentClass="text-emerald-400"
          />
        </div>
      </NumberedInstructionLast>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*  Step 2 — Run PullRequest                                                  */
/* -------------------------------------------------------------------------- */

export function RunPullRequestStep() {
  const host = useResolvedHost();
  return (
    <div className="space-y-0">
      <NumberedInstruction step={1} title="Make sure you are on a feature branch">
        <p className="text-sm leading-7 text-muted-foreground">
          SpecTree PullRequest will not run from the default branch (e.g.{" "}
          <code className="rounded bg-white/[0.06] px-1.5 py-0.5 text-xs text-foreground/80">main</code>).
          Switch to the feature branch where your work lives:
        </p>
        <CopyableTerminal title="Terminal" copyText="git branch --show-current">
          <p>
            <span className="text-foreground/45">$ </span>
            <span className="text-foreground">git branch --show-current</span>
          </p>
          <p className="text-emerald-400">feature/my-epic-implementation</p>
        </CopyableTerminal>
      </NumberedInstruction>

      <NumberedInstruction step={2} title="Invoke the skill">
        <p className="text-sm leading-7 text-muted-foreground">
          In your {getHostLabel(host)} session, invoke the PullRequest skill. It auto-detects
          uncommitted changes, unpushed commits, and whether a PR already exists:
        </p>
        <CopyableTerminal
          title={getHostLabel(host)}
          copyText="Invoke SpecTree-PullRequest for my current branch"
        >
          <p>
            <span className="text-amber-400/70">{">"} </span>
            <span className="text-foreground">Invoke SpecTree-PullRequest for my current branch</span>
          </p>
        </CopyableTerminal>
        <p className="mt-2 text-sm leading-7 text-muted-foreground">
          If you have uncommitted changes, it will stage and commit them — asking you for
          a commit message first. If a PR already exists on this branch, it will use
          that PR instead of creating a new one.
        </p>
      </NumberedInstruction>

      <NumberedInstructionLast step={3} title="The PR description">
        <p className="text-sm leading-7 text-muted-foreground">
          SpecTree PullRequest drafts a structured PR description with four sections:{" "}
          <strong className="text-foreground/90">Summary</strong>,{" "}
          <strong className="text-foreground/90">Changes</strong>,{" "}
          <strong className="text-foreground/90">Motivation</strong>, and{" "}
          <strong className="text-foreground/90">Testing</strong>. It reads your diff and
          commit history to write something meaningful — never an auto-filled placeholder.
        </p>
        <p className="text-sm leading-7 text-muted-foreground">
          If an existing PR has a weak or auto-filled description, it will automatically
          replace it with a properly structured one.
        </p>
      </NumberedInstructionLast>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*  Step 3 — Review cycle details                                             */
/* -------------------------------------------------------------------------- */

export function ReviewCycleStep() {
  const host = useResolvedHost();
  return (
    <div className="space-y-0">
      <NumberedInstruction step={1} title="Watch the iterations">
        <p className="text-sm leading-7 text-muted-foreground">
          Once the PR is created, SpecTree PullRequest requests{" "}
          <strong className="text-foreground/90">@copilot</strong> as a reviewer and waits
          for the review to come back (usually 30 to 120 seconds). It then reads every
          comment and classifies them:
        </p>
        <div className="grid gap-3 md:grid-cols-2">
          <InfoCard
            icon={ShieldCheck}
            title="Must-fix"
            description="Bugs, security issues, logic errors. These get fixed immediately and pushed in the next commit."
            accentClass="text-emerald-400"
          />
          <InfoCard
            icon={MessageSquare}
            title="Style nits"
            description="Formatting preferences and suggestions that are safe to acknowledge without code changes."
            accentClass="text-emerald-400"
          />
        </div>
      </NumberedInstruction>

      <NumberedInstruction step={2} title="Tracking table">
        <p className="text-sm leading-7 text-muted-foreground">
          After each cycle, SpecTree PullRequest shows you a progress table so you can
          see how many comments were found, how many were fixed, and the overall trend:
        </p>
        <CopyableTerminal title={`${getHostLabel(host)} — Iteration tracker`}>
          <div className="space-y-1.5">
            <p className="text-muted-foreground/70">| Iter | Comments | Fixed | Notes            |</p>
            <p className="text-muted-foreground/70">|------|----------|-------|------------------|</p>
            <p className="text-foreground/80">| 1    | 5        | 4     | 1 false positive |</p>
            <p className="text-foreground/80">| 2    | 1        | 1     | edge case fix    |</p>
            <p className="text-emerald-400">| 3    | 0        | 0     | clean review     |</p>
          </div>
        </CopyableTerminal>
      </NumberedInstruction>

      <NumberedInstructionLast step={3} title="When is it done?">
        <p className="text-sm leading-7 text-muted-foreground">
          The PR is only considered complete when{" "}
          <strong className="text-foreground/90">both conditions</strong> are met:
        </p>
        <div className="grid gap-3 md:grid-cols-2">
          <InfoCard
            icon={GitPullRequest}
            title="Copilot review clean"
            description="No actionable comments (must-fix or should-fix) remain after at least 3 review cycles."
            accentClass="text-emerald-400"
          />
          <InfoCard
            icon={CheckCircle2}
            title="CI passing"
            description="GitHub Actions workflows must all pass. If CI fails, SpecTree PullRequest investigates and attempts to fix the issue."
            accentClass="text-emerald-400"
          />
        </div>
      </NumberedInstructionLast>
    </div>
  );
}
